<template>
  <!--
    PoolaProspectRun — the READ-ONLY prospect (non-member) composition (Poola
    two-side launch). The marketing "shop window": any signed-in user who is
    neither an activated seller nor an approved supplier resolves to
    meta.viewerRole:'prospect' (BE-confirmation 2026-07-10) and lands here.

    The sibling of PoolaSupplierRun, but SIMPLER — a prospect has no claims, so
    there is NO منتجاتي segment and NO segment control at all: just العروض,
    read-only, plus a sticky «انضم واحجز» join bar that converts the prospect
    into the registration flow.

    Like the supplier read-only path it NEVER imports the claim sheet, the
    balance pill, or useWallets, and never calls usePoolaOffers().claim() — the
    money move is unreachable (BE also forces canClaim:false + 403s the claim).

    A CONTENT FRAGMENT — never its own <f7-page> (the host owns the page shell;
    nesting a page here would clip the active fragment, playbook Section 5 row 17).

    Disintermediation: BE nulls telegramPostUrl/postContent and omits the post
    for a prospect, so no supplier source link renders (the card null-checks all
    three — see PoolaOfferCard).
  -->
  <div class="poola-prospect-run" :data-phase="phase">
    <!-- hide-budget: a prospect must never see the members-only «بولشير» run-budget
         chip, even if the backend returns a non-null myPoolshareRemainingCents
         ("cap or null" per the prospect contract). Defense-in-depth. -->
    <poola-run-header
      :phase="phase"
      :deal="deal"
      :server-offset="serverOffset"
      hide-budget
    ></poola-run-header>

    <!-- Read-only banner: a prospect views the run but cannot claim
         (prospect POST …/claim → 403). -->
    <div class="poola-prospect-run__banner" role="note">
      {{ t('poola.buy.prospect.readOnlyBanner') }}
    </div>

    <!-- Run-level list states (offersAll-based). -->
    <poola-error v-if="error && !offersAll.length" @retry="loadInitial"></poola-error>

    <poola-skeleton v-else-if="loading && !offersAll.length"></poola-skeleton>

    <template v-else-if="!offersAll.length">
      <div class="poola-prospect-run__empty">
        <p class="poola-prospect-run__empty-title">{{ t('poola.buy.empty.header') }}</p>
        <p class="poola-prospect-run__empty-body">{{ t('poola.buy.empty.body') }}</p>
      </div>

      <!-- No upcoming offers yet: the full recap card under the empty text, with
           the one invitation line a visitor gets. -->
      <poola-last-run-card
        v-if="showLastRun"
        visitor
        :run="lastRun"
        :see-all-href="lastRunHref"
      ></poola-last-run-card>
    </template>

    <template v-else>
      <!-- The last finished Poola, COMPACT, above the upcoming offers (scheduled
           phase only). -->
      <poola-last-run-card
        v-if="showLastRun"
        compact
        visitor
        :run="lastRun"
        :see-all-href="lastRunHref"
      ></poola-last-run-card>

      <!-- Suppliers rail — only worth showing once ≥1 real supplier chip exists
           alongside «الكل» (client-side facet). supplierName/supplierId stay
           present for a prospect (an org id is not a contact path). -->
      <poola-supplier-filter
        v-if="showFilter"
        :suppliers="suppliers"
        :active-supplier="activeSupplier"
        @set-supplier="setSupplier"
      ></poola-supplier-filter>

      <!-- Plain container (NOT f7-list): PoolaOfferCard is a self-contained
           f7-card. read-only → no claim CTA; members-only-tag → the «للأعضاء بس»
           lock tag renders where the CTA would be. -->
      <div class="poola-prospect-run__list">
        <poola-offer-card
          v-for="offer in offers"
          :key="offer.id"
          :offer="offer"
          :phase="phase"
          read-only
          members-only-tag
        ></poola-offer-card>
      </div>

      <!-- Infinite load-more sentinel (IntersectionObserver) + a more-spinner. -->
      <div ref="sentinelRef" class="poola-prospect-run__sentinel" aria-hidden="true" />
      <div v-if="isLoadingMore" class="poola-prospect-run__more">
        <f7-preloader></f7-preloader>
      </div>
    </template>

    <!-- Sticky join bar — the marketing conversion CTA. Rendered OUTSIDE the
         list-state branches so it stays visible in every sub-state (a prospect
         can convert from a live run, a scheduled preview, or an empty window).
         Navigates to the join/registration flow. -->
    <div class="poola-prospect-run__join">
      <p class="poola-prospect-run__join-caption">
        {{ t('poola.buy.prospect.joinSubcaption') }}
      </p>
      <f7-button fill large class="poola-prospect-run__join-btn" @click="goJoin">
        {{ t('poola.buy.prospect.joinCta') }}
      </f7-button>
    </div>
  </div>
</template>

<script setup>
import { f7 } from 'framework7-vue';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import PoolaError from './PoolaError.vue';
import PoolaLastRunCard from './PoolaLastRunCard.vue';
import PoolaOfferCard from './PoolaOfferCard.vue';
import PoolaRunHeader from './PoolaRunHeader.vue';
import PoolaSkeleton from './PoolaSkeleton.vue';
import PoolaSupplierFilter from './PoolaSupplierFilter.vue';
import { usePoolaOffers } from '@/composables/usePoolaOffers.js';

const props = defineProps({
  phase: {
    type: String,
    default: 'active',
    validator: (v) => ['scheduled', 'active', 'ended'].includes(v),
  },
  // The deserialised poola-deals resource (id + flattened attributes).
  deal: {
    type: Object,
    default: null,
  },
  // serverNow − clientNow (ms) from usePoolaDeal — passed through to the header
  // countdown so a skewed phone clock doesn't flip the run early/late.
  serverOffset: {
    type: Number,
    default: 0,
  },
  // usePoolaDeal.previousRun — the last finished run, passed by PoolaPage only on
  // the COMING branch. null (the default) leaves every screen exactly as before.
  lastRun: {
    type: Object,
    default: null,
  },
  // Where the recap card's link goes (the last-Poola page). null → no link.
  lastRunHref: {
    type: String,
    default: null,
  },
});

const { t } = useI18n({ useScope: 'global' });

// filter[deal] is required (a blank id 422s); a getter keeps it reactive if the
// deal id ever changes under us.
const dealId = computed(() => props.deal?.id ?? null);

// NOTE: claim() is intentionally NOT destructured — the prospect path is
// read-only and must never reach the money move (BE 403s it regardless).
// myClaims/myOffers are irrelevant: a prospect holds nothing.
const {
  offers,
  offersAll,
  suppliers,
  activeSupplier,
  setSupplier,
  hasMore,
  isLoadingMore,
  loading,
  error,
  loadInitial,
  loadMore,
} = usePoolaOffers(dealId);

// Show the rail only when there's a real supplier chip beyond «الكل».
const showFilter = computed(() => suppliers.value.length > 1);

// Last-Poola recap card: scheduled phase + a previous run handed down by the host.
// Loading or a first-load error with no offers renders no card.
const showLastRun = computed(() => props.phase === 'scheduled' && props.lastRun != null);

// ── Join CTA ──────────────────────────────────────────────────────────────────
// The onboarding entry (ClubChoiceScreen). Kept as a literal to avoid a circular
// import back through router/routes.js (which eagerly imports PoolaPage → this).
// Mirrors the hardcoded-route pattern in UnderReviewScreen.vue. Canonical SSOT is
// ONBOARDING_ENTRY in src/router/routes.js — keep in sync if it ever changes.
const ONBOARDING_ENTRY = '/join/';

// Route into the onboarding flow. A prospect with no membership lands on the
// club-choice screen; the /join/ guards bounce a would-be member to their shell
// (redirectMembersToShell) — no routing restructure needed here.
function goJoin() {
  const router = f7?.views?.main?.router;
  if (!router) return;
  router.navigate(ONBOARDING_ENTRY);
}

// ── Infinite load-more (IntersectionObserver) ────────────────────────────────
// Same wiring as PoolaRun/PoolaSupplierRun: the sentinel only exists once offers
// render, so the watch re-binds the observer when it mounts/unmounts.
const sentinelRef = ref(null);
let observer = null;

function maybeLoadMore() {
  if (hasMore.value && !loading.value && !isLoadingMore.value) loadMore();
}

function ensureObserver() {
  if (observer || typeof IntersectionObserver === 'undefined') return;
  observer = new IntersectionObserver(
    (entries) => {
      if (entries.some((e) => e.isIntersecting)) maybeLoadMore();
    },
    { rootMargin: '200px' },
  );
}

watch(sentinelRef, (el) => {
  ensureObserver();
  if (!observer) return;
  observer.disconnect();
  if (el) observer.observe(el);
});

onMounted(() => {
  loadInitial();
  // No wallet read on the prospect path — there is no affordability surface.
});

onUnmounted(() => {
  observer?.disconnect();
  observer = null;
});

// Exposed for the host / tests to drive load-more without the observer.
defineExpose({ loadMore: maybeLoadMore });
</script>

<style scoped>
.poola-prospect-run {
  padding-bottom: 24px;
}

/* ── Read-only banner ─────────────────────────────────────────────────────── */
.poola-prospect-run__banner {
  margin: 0 16px 12px;
  padding: 10px 14px;
  background: var(--poolit-primary-50);
  color: var(--poolit-primary-700);
  border-radius: var(--poolit-radius-md);
  font-size: var(--poolit-font-size-small);
  font-weight: var(--poolit-font-weight-medium);
  text-align: center;
  line-height: var(--poolit-line-height-body);
}

/* ── Offers list ──────────────────────────────────────────────────────────── */
.poola-prospect-run__list {
  display: flex;
  flex-direction: column;
}

.poola-prospect-run__sentinel {
  height: 1px;
}

.poola-prospect-run__more {
  display: flex;
  justify-content: center;
  padding: 12px 0;
}

/* ── Empty / run-level states ─────────────────────────────────────────────── */
.poola-prospect-run__empty {
  text-align: center;
  padding: 48px 28px;
}

.poola-prospect-run__empty-title {
  color: var(--poolit-text-primary);
  font-size: var(--poolit-font-size-body);
  font-weight: var(--poolit-font-weight-semibold);
  margin: 0 0 8px;
}

.poola-prospect-run__empty-body {
  color: var(--poolit-text-muted);
  font-size: var(--poolit-font-size-small);
  margin: 0;
  max-width: 300px;
  margin-inline: auto;
}

/* ── Sticky join bar ──────────────────────────────────────────────────────── */
/* sticky bottom:0 pins the bar to the bottom of the page-content scrollport
   while the list scrolls under it, and rests at its natural spot once scrolled
   to the very bottom. /poola/ is a STANDALONE pushed page (PoolaPage renders no
   <tabbar-navigation> — only the BasePage tab shell does), so no shell tabbar
   sits under this bar for any viewer; the only bottom inset to clear is the home
   indicator (env(safe-area-inset-bottom)). */
.poola-prospect-run__join {
  position: sticky;
  bottom: 0;
  z-index: 10;
  margin-top: 12px;
  padding: 14px 16px calc(env(safe-area-inset-bottom, 0px) + 14px);
  background: var(--poolit-bg-primary);
  border-top: 1px solid var(--poolit-border-color);
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.06);
}

.poola-prospect-run__join-caption {
  margin: 0 0 10px;
  font-size: var(--poolit-font-size-small);
  line-height: var(--poolit-line-height-body);
  color: var(--poolit-text-secondary);
  text-align: center;
}

.poola-prospect-run__join-btn {
  --f7-button-fill-bg-color: var(--poolit-primary-600);
  --f7-button-fill-text-color: #fff;
  --f7-button-fill-hover-bg-color: var(--poolit-primary-700);
  width: 100%;
}

/* Ended runs render greyed + read-only (locked design, mirrors PoolaRun /
   PoolaSupplierRun). Direct-child selector dims EVERY run element uniformly
   (header, banner, filter rail, list) EXCEPT the join bar, which stays fully
   legible — it is the conversion CTA, not run content. */
.poola-prospect-run[data-phase='ended'] > *:not(.poola-prospect-run__join) {
  opacity: 0.55;
}
</style>
